<template>
  <div class="mt-2">
    <h3 class="mb-4">
      {{ t('myStoreMenu.myNotifications') }}
    </h3>
    <div class="grid">
      <div class="col-12">
        <div class="filter-section mb-4">
          <div class="filter-buttons">
            <Button
              v-for="category in notificationCategories"
              :key="category.value"
              :class="['filter-btn', { active: selectedCategory === category.value }]"
              @click="selectedCategory = category.value"
              text
            >
              <i :class="category.icon" />
              <span class="ml-2">{{ category.label }} ({{ getCategoryCount(category.value) }})</span>
            </Button>
          </div>
        </div>

        <Message
          v-if="notifications.length === 0 && !loadingHasMore"
          severity="info"
          :closable="false"
        >
          {{ t('noRecords') }}
        </Message>

        <div v-if="notifications.length > 0" class="notifications-container">
          <div
            v-for="notification of filteredNotifications"
            :key="notification.id_shop_notification"
            class="notification-wrapper"
          >
            <div 
              class="notification-card" 
              :class="[
                `severity-${getCategoryForType(notification.type)}`,
              ]"
            >
              <div class="notification-header">
                <div class="header-left">
                  <div class="severity-indicator">
                    <i class="fas fa-bell" />
                  </div>
                  <div class="header-content">
                    <div class="notification-title" v-html="notification.title" />
                    <div class="notification-meta">
                      <span class="notification-date">
                        <i class="far fa-clock" />
                        {{ formatDate(notification.date) }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div class="notification-body">
                <div
                  class="notification-content"
                  v-html="notification.message"
                />
              </div>
            </div>
          </div>

          <Message
            v-if="filteredNotifications.length === 0"
            severity="info"
            :closable="false"
          >
            {{ t('noRecords') }}
          </Message>
        </div>

        <div 
          v-if="hasMore" 
          ref="scrollSentinel" 
          class="scroll-sentinel"
        >
          <div v-if="loadingHasMore" class="skeleton-loader">
            <div class="notification-card">
              <div class="notification-header">
                <div class="header-left">
                  <div class="skeleton-severity-indicator"></div>
                  <div class="header-content">
                    <div class="skeleton-notification-title"></div>
                    <div class="notification-meta">
                      <span class="skeleton-notification-date"></span>
                    </div>
                  </div>
                </div>
              </div>
              <div class="notification-body">
                <div class="skeleton-notification-content">
                  <div class="skeleton-line"></div>
                  <div class="skeleton-line" style="width: 70%;"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { useI18n } from 'vue-i18n';
import Card from 'primevue/card';
import Button from 'primevue/button';
import Message from 'primevue/message';
import {
  defineComponent, ref, onMounted, computed, onBeforeUnmount, nextTick,
} from 'vue';
import {
  fetchNotifications, markNotificationsAsRead,
} from '@/composables/shop/Notifications';
import { UserState } from '@/composables/User';
import {
  ShopsNotifications,
  ShopsNotificationsReadInputItem,
} from '@/types/generated-types/graphql';
import { showToastError } from '@/helpers';
import Tooltip from 'primevue/tooltip';

interface NotificationCategory {
  value: string;
  label: string;
  icon: string;
  types: string[];
}

export default defineComponent({
  name: 'MyNotifications',
  components: {
    Card,
    Button,
    Message,
  },
  directives: {
    tooltip: Tooltip,
  },
  setup() {
    const { t } = useI18n();
    const notifications: any = ref<ShopsNotifications[]>([]);
    const shopId = UserState?.activeShop?.id ?? 0;
    const paging = ref<number>(0);
    const hasMore = ref(true);
    const loadingHasMore = ref(false);
    const selectedCategory = ref<string>('all');
    const scrollSentinel = ref<HTMLElement | null>(null);
    let observer: IntersectionObserver | null = null;

    const notificationCategories: NotificationCategory[] = [
      {
        value: 'all',
        label: t('notification.all'),
        icon: 'fas fa-list',
        types: ['low', 'normal', 'important'],
      },
      {
        value: 'success',
        label: t('notification.low'),
        icon: 'fas fa-info-circle',
        types: ['low'],
      },
      {
        value: 'warning',
        label: t('notification.normal'),
        icon: 'fas fa-exclamation-circle',
        types: ['normal'],
      },
      {
        value: 'error',
        label: t('notification.high'),
        icon: 'fas fa-exclamation-triangle',
        // types: ['high'],
        types: ['important'],
      },
    ];

    const getNotifications = async () => {
      loadingHasMore.value = true;
      const getNotificationsItems = await fetchNotifications({ shopId, paging: paging.value });
      loadingHasMore.value = false;

      if (getNotificationsItems && getNotificationsItems.length === 0) {
        hasMore.value = false;
        return;
      }

      notifications.value = notifications.value.concat(getNotificationsItems);
    };

    const fetchMore = async () => {
      paging.value += 1;
      await getNotifications();
    };

    const markAllAsRead = async () => {
      const notificationsReadRecords: ShopsNotificationsReadInputItem[] = [];
      notifications.value.forEach((item: ShopsNotifications) => {
        if (item.shopsNotificationsRead?.length === 0) {
          notificationsReadRecords.push({
            id_shop_notification: item.id_shop_notification,
            id_shop: shopId,
            date: new Date(),
          });
        }
      });

      if (notificationsReadRecords.length > 0) {
        await markNotificationsAsRead(notificationsReadRecords);
      }
    };

    const getCategoryForType = (type: string): string => {
      switch (type) {
        case 'low':
          return 'success';
        case 'normal':
          return 'warning';
        // case 'high':
        case 'important':
          return 'error';
        default:
          return 'info';
      }
    };

    const getCategoryLabel = (type: string): string => {
      const category = notificationCategories.find((cat) => cat.types.includes(type));
      return category ? category.label : 'Autre';
    };

    const formatDate = (date: any): string => {
      if (!date) return '';
      const dateObj = new Date(date);
      return dateObj.toLocaleString('fr-FR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    };

    const filteredNotifications = computed(() => {
      const selectedCat = notificationCategories.find((cat) => cat.value === selectedCategory.value);
      if (!selectedCat) return notifications.value;
      return notifications.value.filter((notif: ShopsNotifications) => selectedCat.types.includes(notif.type));
    });

    const getCategoryCount = (categoryValue: string): number => {
      const category = notificationCategories.find((cat) => cat.value === categoryValue);
      if (!category) return 0;
      if (categoryValue === 'all') return notifications.value.length;
      return notifications.value.filter((notif: ShopsNotifications) => category.types.includes(notif.type)).length;
    };

    const downloadFile = async (event: Event) => {
      event.preventDefault();

      const apiUrl = (event.currentTarget as HTMLAnchorElement).href;
      const apiToken = 'Ae1i2kndasdcasdfe23sad';

      try {
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'api-token': apiToken,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to download file, invalid token or server error');
        }

        const blob = await response.blob();

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'folie';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error:', error);
        await showToastError('Failed to download file.');
      }
    };

    onMounted(async () => {
      await getNotifications();
      await markAllAsRead();

      // Setup Intersection Observer for infinite scroll
      // Use nextTick to ensure DOM is ready
      await nextTick();
      
      if (scrollSentinel.value && hasMore.value) {
        observer = new IntersectionObserver(
          (entries) => {
            const [entry] = entries;
            if (entry.isIntersecting && hasMore.value && !loadingHasMore.value) {
              fetchMore();
            }
          },
          {
            root: null,
            rootMargin: '200px', // Start loading 200px before reaching the sentinel
            threshold: 0,
          },
        );

        observer.observe(scrollSentinel.value);
      }
    });

    onBeforeUnmount(() => {
      if (observer && scrollSentinel.value) {
        observer.unobserve(scrollSentinel.value);
        observer.disconnect();
      }
    });

    return {
      t,
      notifications,
      fetchMore,
      hasMore,
      loadingHasMore,
      downloadFile,
      notificationCategories,
      selectedCategory,
      filteredNotifications,
      getCategoryForType,
      getCategoryLabel,
      formatDate,
      getCategoryCount,
      scrollSentinel,
    };
  },
});
</script>

<style lang="scss" scoped>
.xx-large {
  font-size: xx-large;
}

.filter-section {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 12px;
  margin-bottom: 24px;

  .filter-buttons {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    width: 100%;
  }
}

.filter-btn {
  padding: 10px 18px !important;
  border-radius: 24px !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  transition: all 0.2s ease !important;
  background: white !important;
  border: 2px solid #e0e0e0 !important;
  color: #666 !important;

  &:hover {
    background: #f8f9fa !important;
    border-color: #94c840 !important;
    transform: translateY(-1px);
  }

  &.active {
    background: linear-gradient(135deg, #94c840 0%, #7aa830 100%) !important;
    color: white !important;
    border-color: #94c840 !important;
  }
}

.notifications-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.notification-wrapper {
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.notification-card {
  position: relative;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s ease;
  border: 1px solid #e8e8e8;

  &:hover {
    transform: translateY(-2px);

    .header-actions {
      opacity: 1;
    }
  }

  &.unread {
    border-left: 4px solid #94c840;
    background: #fafffe;
  }

  &.severity-success {
    .severity-indicator {
      background: linear-gradient(135deg, #10b981 0%, #059669 100%);
      color: white;
    }
  }

  &.severity-warning {
    .severity-indicator {
      background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
      color: white;
    }
  }

  &.severity-error {
    .severity-indicator {
      background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
      color: white;
    }
  }

  &.severity-info {
    .severity-indicator {
      background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
      color: white;
    }
  }
}

.notification-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 16px 20px;
  background: #fafafa;
  border-bottom: 1px solid #f0f0f0;
  gap: 12px;
}

.header-left {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  flex: 1;
  min-width: 0;
}

.severity-indicator {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.3s ease;

  i {
    font-size: 16px;
    color: white;
  }

  .notification-card:hover & {
    transform: scale(1.08);
  }
}

.header-content {
  flex: 1;
  min-width: 0;
}

.notification-title {
  font-size: 15px;
  font-weight: 600;
  color: #2c3e50;
  line-height: 1.4;
  margin-bottom: 6px;
  word-wrap: break-word;

  ::v-deep(strong) {
    color: #1a252f;
  }
}

.notification-meta {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.notification-date {
  font-size: 12px;
  color: #8898aa;
  display: flex;
  align-items: center;
  gap: 6px;

  i {
    font-size: 11px;
  }
}

.action-btn {
  width: 36px !important;
  height: 36px !important;
  padding: 0 !important;
  transition: all 0.2s ease !important;

  &:hover {
    transform: scale(1.1);
  }

  &.mark-read-btn {
    color: #94c840 !important;

    &:hover {
      background: #f0f8e8 !important;
    }
  }
}

.notification-body {
  padding: 18px 20px;
}

.notification-content {
  font-size: 14px;
  line-height: 1.7;
  color: #525f7f;
  word-wrap: break-word;

  ::v-deep(p) {
    margin: 0 0 10px 0;

    &:last-child {
      margin-bottom: 0;
    }
  }

  ::v-deep(a) {
    color: #94c840 !important;
    text-decoration: none;
    font-weight: 600;
    transition: all 0.2s ease;

    &:hover {
      color: #7aa830 !important;
      text-decoration: underline;
    }
  }

  ::v-deep(strong) {
    color: #2c3e50;
    font-weight: 600;
  }
}

.filter-buttons .p-button:focus {
  box-shadow: none !important;
}

.filter-buttons .p-button:focus-visible {
  outline: 2px solid #94c840 !important;
  outline-offset: 2px !important;
}

@media (max-width: 768px) {
  .notification-header {
    padding: 14px 16px;
  }

  .severity-indicator {
    width: 38px;
    height: 38px;

    i {
      font-size: 16px;
    }
  }

  .notification-title {
    font-size: 14px;
  }

  .notification-body {
    padding: 14px 16px;
  }

  .header-actions {
    opacity: 1;
  }
}

::v-deep(.p-timeline-event) {
  .content {
    a {
      color: #94c840 !important;
      &:hover {
        color: #7aa830 !important;
      }
    }
  }
}

.scroll-sentinel {
  min-height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px 0;
  margin: 0;
}

.skeleton-loader {
  width: 100%;
  margin: 12px 0 0 0;
  padding: 0;
  
  .notification-card {
    background: white;
    border-radius: 12px;
    border: 1px solid #e8e8e8;
    overflow: hidden;
    width: 100%;
    
    .notification-header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      padding: 16px 20px;
      background: #fafafa;
      border-bottom: 1px solid #f0f0f0;
      gap: 12px;
      
      .header-left {
        display: flex;
        align-items: flex-start;
        gap: 14px;
        flex: 1;
        min-width: 0;
        
        .skeleton-severity-indicator {
          width: 36px;
          height: 36px;
          // background: linear-gradient(90deg, #f0f0f0 0%, #f0f0f0 40%, #e0e0e0 50%, #f0f0f0 60%, #f0f0f0 100%);
          background: #f0f0f0;
          background-size: 200% 100%;
          border-radius: 10px;
          flex-shrink: 0;
          // animation: shimmer 2s ease-in-out infinite;
        }
        
        .header-content {
          flex: 1;
          min-width: 0;
          
          .skeleton-notification-title {
            height: 21px;
            width: 70%;
            background: linear-gradient(90deg, #f0f0f0 0%, #f0f0f0 40%, #e0e0e0 50%, #f0f0f0 60%, #f0f0f0 100%);
            background-size: 200% 100%;
            border-radius: 4px;
            margin-bottom: 6px;
            animation: shimmer 2.5s linear infinite;
          }
          
          .notification-meta {
            display: flex;
            align-items: center;
            gap: 12px;
            flex-wrap: wrap;
            
            .skeleton-notification-date {
              display: inline-block;
              width: 120px;
              height: 12px;
              background: linear-gradient(90deg, #f0f0f0 0%, #f0f0f0 40%, #e0e0e0 50%, #f0f0f0 60%, #f0f0f0 100%);
              background-size: 200% 100%;
              border-radius: 3px;
              animation: shimmer 2.5s linear infinite;
            }
          }
        }
      }
    }
    
    .notification-body {
      padding: 18px 20px;
      
      .skeleton-notification-content {
        .skeleton-line {
          height: 14px;
          background: linear-gradient(90deg, #f0f0f0 0%, #f0f0f0 40%, #e0e0e0 50%, #f0f0f0 60%, #f0f0f0 100%);
          background-size: 200% 100%;
          border-radius: 3px;
          margin-bottom: 10px;
          animation: shimmer 2.5s linear infinite;
          
          &:last-child {
            width: 70%;
            margin-bottom: 0;
          }
        }
      }
    }
  }
  
  @keyframes shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
</style>
